import { zonedInstant } from '@glory/kernel';

import { type DealSource, type IsoDate } from './deal.ts';
import { addDays } from './step.ts';

/** Месяц `ГГГГ-ММ`: сводка считается по календарным месяцам пояса студии. */
export type Month = string;

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const YEAR_MIN = 2000;
const YEAR_MAX = 2100;

/** Корректный месяц сводки: форма `ГГГГ-ММ`, год в разумных пределах. */
export function isMonth(value: string): value is Month {
  const match = MONTH_PATTERN.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  return year >= YEAR_MIN && year <= YEAR_MAX;
}

/** Месяц, в который попадает календарная дата. */
export const monthOfDate = (date: IsoDate): Month => date.slice(0, 7);

/** Сдвиг месяца: `-1` — прошлый, `1` — следующий; год переходит сам. */
export function addMonths(month: Month, delta: number): Month {
  const [year = 0, mon = 1] = month.split('-').map(Number);
  const index = year * 12 + (mon - 1) + delta;
  const nextYear = Math.floor(index / 12);
  return `${String(nextYear).padStart(4, '0')}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/**
 * Границы месяца в поясе студии: с полуночи первого числа (включительно) до полуночи первого числа
 * следующего месяца (не включая). Запись, созданная в 23:30 по Москве 31-го, — в этом месяце, хотя по UTC
 * уже в следующем.
 */
export function monthBounds(month: Month, timeZone: string): { readonly from: Date; readonly to: Date } {
  return {
    from: zonedInstant(timeZone, `${month}-01`, 0),
    to: zonedInstant(timeZone, `${addMonths(month, 1)}-01`, 0),
  };
}

/** Число дней в месяце: для подписей и проверок. */
export function daysInMonth(month: Month): number {
  const next = `${addMonths(month, 1)}-01`;
  return Number(addDays(next, -1).slice(8, 10));
}

// --- подсчёт --------------------------------------------------------------------------------------

/** Строка группировки лидов: вторичное ли обращение, откуда пришёл, сколько таких. */
export interface LeadGroupRow {
  readonly repeat: boolean;
  readonly source: DealSource | null;
  readonly count: number;
}

export interface LeadTotals {
  readonly total: number;
  /** Первичные обращения. */
  readonly primary: number;
  /** Вторичные: клиент уже был (у него была другая сделка или лид). */
  readonly repeat: number;
  /** По источникам; «Не указан» — `source: null` — всегда в конце, остальные по убыванию. */
  readonly bySource: { readonly source: DealSource | null; readonly count: number }[];
}

/** Лиды за месяц: первичные и вторичные, по источникам, «Не указан» последним. */
export function leadTotals(rows: readonly LeadGroupRow[]): LeadTotals {
  let primary = 0;
  let repeat = 0;
  const bySource = new Map<DealSource | null, number>();
  for (const row of rows) {
    if (row.repeat) repeat += row.count;
    else primary += row.count;
    bySource.set(row.source, (bySource.get(row.source) ?? 0) + row.count);
  }
  const sources = [...bySource.entries()]
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => Number(a.source === null) - Number(b.source === null) || b.count - a.count);
  return { total: primary + repeat, primary, repeat, bySource: sources };
}

/** Строка группировки закрытых сделок: итог, причина, сколько и сумма примерных бюджетов в копейках. */
export interface ClosedGroupRow {
  readonly outcome: 'won' | 'lost';
  readonly reasonId: string | null;
  readonly count: number;
  readonly budget: number;
}

export interface OutcomeTotals {
  readonly count: number;
  /** Сумма примерных бюджетов в копейках; сделки без бюджета в сумму не входят. */
  readonly budget: number;
}

export interface ReasonTotals extends OutcomeTotals {
  readonly reasonId: string | null;
}

export interface ClosedTotals {
  readonly won: OutcomeTotals;
  readonly lost: OutcomeTotals;
  /** Причины проигрыша: чаще других — первыми; проигрыш без причины — в конце. */
  readonly lostReasons: ReasonTotals[];
}

/** Выиграно и проиграно за месяц: количество и сумма бюджетов; причины проигрыша. */
export function closedTotals(rows: readonly ClosedGroupRow[]): ClosedTotals {
  const won = { count: 0, budget: 0 };
  const lost = { count: 0, budget: 0 };
  const reasons = new Map<string | null, OutcomeTotals>();
  for (const row of rows) {
    const sum = row.outcome === 'won' ? won : lost;
    sum.count += row.count;
    sum.budget += row.budget;
    if (row.outcome === 'lost') {
      const own = reasons.get(row.reasonId) ?? { count: 0, budget: 0 };
      reasons.set(row.reasonId, { count: own.count + row.count, budget: own.budget + row.budget });
    }
  }
  const lostReasons = [...reasons.entries()]
    .map(([reasonId, totals]) => ({ reasonId, ...totals }))
    .sort((a, b) => Number(a.reasonId === null) - Number(b.reasonId === null) || b.count - a.count);
  return { won, lost, lostReasons };
}
